"use client";

/**
 * A text field that suggests what is already in the database (Gordon
 * 2026-11-03: "all fields as possible database driven in all edit windows").
 * Distinct values of one or more Firestore fields (a release's label from the
 * labels register, publishers and copyright lines already used, event venues…),
 * read once per visit and filtered as you type. Free text still works — a new
 * value is shown as "Add …". An inline list, not a popover, so it works inside
 * dialogs. Picking a value from a source with ids (labels) also hands over the id.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { collection, getDocs, query, where, type QueryConstraint } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface SuggestSource {
  collection: string;
  field: string;
  /** Only docs where `where[0] == where[1]` (e.g. ["accountType", "venue"]). */
  where?: [string, string];
  /** Picking from this source also gives the doc id (labels → labelId). */
  withId?: boolean;
}
type Option = { value: string; id?: string };

const cache = new Map<string, Promise<Option[]>>();

function load(src: SuggestSource): Promise<Option[]> {
  const key = `${src.collection}.${src.field}.${src.where?.join("=") || ""}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = (async () => {
      try {
        const c: QueryConstraint[] = src.where ? [where(src.where[0], "==", src.where[1])] : [];
        const snap = await getDocs(query(collection(db, src.collection), ...c));
        const out: Option[] = [];
        snap.forEach(d => {
          const v = d.get(src.field);
          for (const s of Array.isArray(v) ? v : [v]) {
            const t = typeof s === "string" ? s.trim() : "";
            if (t) out.push({ value: t, ...(src.withId ? { id: d.id } : {}) });
          }
        });
        return out;
      } catch { return []; }
    })();
    cache.set(key, hit);
  }
  return hit;
}

export function DbSuggestInput({ value, onChange, onPick, sources, extra = [], placeholder, className }: {
  value: string;
  onChange: (v: string) => void;
  /** A suggestion was picked (with its doc id when the source has one). */
  onPick?: (o: Option) => void;
  sources: SuggestSource[];
  /** Always-offered values (e.g. common currencies). */
  extra?: string[];
  placeholder?: string;
  className?: string;
}) {
  const t = useTranslations("common");
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const loaded = useRef(false);
  const box = useRef<HTMLDivElement>(null);

  const fetchAll = () => {
    if (loaded.current) return;
    loaded.current = true;
    void Promise.all(sources.map(load)).then(lists => {
      const seen = new Map<string, Option>();
      for (const o of [...lists.flat(), ...extra.map((v): Option => ({ value: v }))]) {
        const k = o.value.toLowerCase();
        const prev = seen.get(k);
        if (!prev || (!prev.id && o.id)) seen.set(k, o);
      }
      setOptions([...seen.values()].sort((a, b) => a.value.localeCompare(b.value)));
    });
  };
  useEffect(() => { if (open) fetchAll(); }); // eslint-disable-line react-hooks/exhaustive-deps

  const q = value.trim().toLowerCase();
  const list = useMemo(() => options.filter(o => !q || o.value.toLowerCase().includes(q)).slice(0, 12), [options, q]);
  const exact = options.some(o => o.value.toLowerCase() === q);

  return (
    <div ref={box} className="relative" onBlur={e => { if (!box.current?.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <Input value={value} placeholder={placeholder} className={className} autoComplete="off" role="combobox" aria-expanded={open} aria-autocomplete="list"
        onFocus={() => setOpen(true)} onChange={e => { onChange(e.target.value); setOpen(true); }}
        onKeyDown={e => { if (e.key === "Escape") setOpen(false); }} />
      {open && (list.length > 0 || (q && !exact)) && (
        <ul role="listbox" className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md">
          {list.map(o => (
            <li key={`${o.id || ""}:${o.value}`}>
              <button type="button" className={cn("w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground", o.value === value && "font-semibold")}
                onMouseDown={e => { e.preventDefault(); onChange(o.value); onPick?.(o); setOpen(false); }}>
                {o.value}
              </button>
            </li>
          ))}
          {q && !exact && (
            <li className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">{t("addValue", { value: value.trim() })}</li>
          )}
        </ul>
      )}
    </div>
  );
}
